import getDecorators from "inversify-inject-decorators";
import container from "../../ioc/inversify.config";
import TYPES from "../../ioc/types";
import { ThemeManager } from "../design-system/theme-manager";

const { lazyInject } = getDecorators(container);

/**
 * LevelUpNotification - Component for displaying level-up notifications after battle
 * Shows character name, new level, and stat increases
 */
export class LevelUpNotification extends Phaser.GameObjects.Container {
    @lazyInject(TYPES.ThemeManager)
    private themeManager: ThemeManager;

    private background: Phaser.GameObjects.Rectangle;
    private border: Phaser.GameObjects.Rectangle;
    private titleText: Phaser.GameObjects.Text;
    private levelText: Phaser.GameObjects.Text;
    private statTexts: Phaser.GameObjects.Text[] = [];
    private closeText: Phaser.GameObjects.Text;

    constructor(
        scene: Phaser.Scene,
        x: number,
        y: number,
        characterName: string,
        newLevel: number,
        statIncreases: { [statId: string]: number }
    ) {
        super(scene, x, y);

        // Get theme manager instance
        const themeManager = container.get<ThemeManager>(TYPES.ThemeManager);

        // Panel dimensions
        const panelWidth = 400;
        const panelHeight = 300;

        // Background panel
        this.background = scene.add.rectangle(
            0,
            0,
            panelWidth,
            panelHeight,
            themeManager.getColor("ui.backgroundDark"),
            0.95
        );
        this.add(this.background);

        // Border
        this.border = scene.add.rectangle(0, 0, panelWidth, panelHeight);
        this.border.setStrokeStyle(
            themeManager.getBorderWidth("thick"),
            themeManager.getColor("accent.yellow")
        );
        this.border.setFillStyle(0x000000, 0);
        this.add(this.border);

        // Title text - "LEVEL UP!"
        this.titleText = scene.add.text(0, -120, "LEVEL UP!", {
            fontSize: themeManager.getFontSize("xxl"),
            color: themeManager.getColorString("accent.yellow"),
            fontFamily: themeManager.getFontFamily(),
            fontStyle: "bold",
            resolution: 2,
            stroke: themeManager.getColorString("ui.backgroundDark"),
            strokeThickness: themeManager.getStrokeWidth("thick"),
        });
        this.titleText.setOrigin(0.5);
        this.titleText.updateText();
        this.add(this.titleText);

        // Character name and level
        this.levelText = scene.add.text(0, -80, `${characterName} reached Level ${newLevel}!`, {
            fontSize: themeManager.getFontSize("lg"),
            color: themeManager.getColorString("text.primary"),
            fontFamily: themeManager.getFontFamily(),
            resolution: 2,
            stroke: themeManager.getColorString("ui.backgroundDark"),
            strokeThickness: themeManager.getStrokeWidth("normal"),
        });
        this.levelText.setOrigin(0.5);
        this.levelText.updateText();
        this.add(this.levelText);

        // Stat increases
        let yOffset = -40;
        const statEntries = Object.entries(statIncreases);

        if (statEntries.length > 0) {
            for (const [statId, increase] of statEntries) {
                const statText = scene.add.text(
                    0,
                    yOffset,
                    `${this.formatStatName(statId)}: +${increase}`,
                    {
                        fontSize: themeManager.getFontSize("base"),
                        color: themeManager.getColorString("accent.green"),
                        fontFamily: themeManager.getFontFamily(),
                        resolution: 2,
                        stroke: themeManager.getColorString("ui.backgroundDark"),
                        strokeThickness: themeManager.getStrokeWidth("normal"),
                    }
                );
                statText.setOrigin(0.5);
                statText.updateText();
                this.statTexts.push(statText);
                this.add(statText);
                yOffset += 30;
            }
        } else {
            // No stat increases message
            const noStatsText = scene.add.text(0, yOffset, "Level increased!", {
                fontSize: themeManager.getFontSize("base"),
                color: themeManager.getColorString("text.secondary"),
                fontFamily: themeManager.getFontFamily(),
                resolution: 2,
                stroke: themeManager.getColorString("ui.backgroundDark"),
                strokeThickness: themeManager.getStrokeWidth("normal"),
            });
            noStatsText.setOrigin(0.5);
            noStatsText.updateText();
            this.statTexts.push(noStatsText);
            this.add(noStatsText);
        }

        // Close button hint
        this.closeText = scene.add.text(0, 120, "[Press SPACE to continue]", {
            fontSize: themeManager.getFontSize("sm"),
            color: themeManager.getColorString("text.secondary"),
            fontFamily: themeManager.getFontFamily(),
            resolution: 2,
            stroke: themeManager.getColorString("ui.backgroundDark"),
            strokeThickness: themeManager.getStrokeWidth("normal"),
        });
        this.closeText.setOrigin(0.5);
        this.closeText.updateText();
        this.add(this.closeText);

        // Add to scene
        scene.add.existing(this);

        // Set high depth to appear above other UI
        this.setDepth(1000);

        // Fade in animation
        this.setAlpha(0);
        scene.tweens.add({
            targets: this,
            alpha: 1,
            duration: themeManager.getDuration("normal"),
            ease: themeManager.getEasing("easeInOut"),
        });
    }

    /**
     * Format stat ID to display name
     * e.g., "hp" -> "HP", "max_hp" -> "Max HP"
     */
    private formatStatName(statId: string): string {
        return statId
            .split("_")
            .map((word) => word.toUpperCase())
            .join(" ");
    }

    /**
     * Wait for user to press SPACE to continue
     * Returns a promise that resolves when SPACE is pressed
     */
    public waitForConfirm(): Promise<void> {
        return new Promise((resolve) => {
            const spaceKey = this.scene.input.keyboard.addKey("SPACE");
            spaceKey.once("down", () => {
                this.close(resolve);
            });
        });
    }

    /**
     * Close the notification with fade out animation
     */
    private close(callback: () => void): void {
        const themeManager = container.get<ThemeManager>(TYPES.ThemeManager);

        this.scene.tweens.add({
            targets: this,
            alpha: 0,
            duration: themeManager.getDuration("fast"),
            ease: themeManager.getEasing("easeInOut"),
            onComplete: () => {
                this.destroy();
                callback();
            },
        });
    }

    /**
     * Clean up resources
     */
    public destroy(): void {
        this.statTexts = [];
        super.destroy();
    }
}
